import { FC, useState } from 'react';
import { AllieButton, AllieText } from '../../../components';
import {
  ConfirmSummaryCard,
  ConfirmSummaryLabel,
  ConfirmSummaryRow,
  ConfirmSummaryValue,
  ConfirmSummaryValueMuted,
  RequiredAsterisk,
  TextAreaNoMargin,
} from '../../forms/AssignReasonForm/AssignReasonForm.styles';
import {
  ContentLoadingStyled,
  NoteContainer,
  QuickConfirmFooter,
  StyledCancelButton,
  SubtitleText,
} from './EfficiencyWizard.styles';
import { LastReasonsAssign } from '../../forms';
import { isTablet } from '../../../common/utils';
import { StopTypes } from '../../forms/AssignReasonForm/AssignReasons.enum';

interface EfficiencyQuickConfirmCheckProps {
  onGoPrev: () => void;
  onConfirm: (note?: string) => void;
  reasonSelected: LastReasonsAssign | undefined;
  commentRequired?: boolean;
  labels: {
    back: string;
    confirm: string;
    subtitle: string;
    planned: string;
    unplanned: string;
    note: string;
    notePlaceholder: string;
    labelTypeStop: string;
    labelMachine: string;
    labelMotive: string;
    labelGroup: string;
    labelFailureMode: string;
    labelNotApplicable: string;
  };
}

export const EfficiencyQuickConfirmCheck: FC<EfficiencyQuickConfirmCheckProps> = ({
  onGoPrev,
  onConfirm,
  reasonSelected,
  commentRequired,
  labels,
}) => {
  const [note, setNote] = useState<string | undefined>();
  const notApplicable = labels.labelNotApplicable;
  const isNoteValid = !commentRequired || (note != null && note.trim() !== '');
  const stopTypeLabel =
    reasonSelected?.stopType === StopTypes.planned ? labels.planned : labels.unplanned;

  return (
    <ContentLoadingStyled>
      {labels.subtitle && (
        <SubtitleText>
          <AllieText type="body2" color="neutral-50">
            {labels.subtitle}
          </AllieText>
        </SubtitleText>
      )}

      <ConfirmSummaryCard>
        <ConfirmSummaryRow>
          <ConfirmSummaryLabel>{labels.labelTypeStop}</ConfirmSummaryLabel>
          <ConfirmSummaryValue>{stopTypeLabel}</ConfirmSummaryValue>
        </ConfirmSummaryRow>
        <ConfirmSummaryRow>
          <ConfirmSummaryLabel>{labels.labelMachine}</ConfirmSummaryLabel>
          {reasonSelected?.lastAssignedMachineValue ? (
            <ConfirmSummaryValue>{reasonSelected.lastAssignedMachineValue}</ConfirmSummaryValue>
          ) : (
            <ConfirmSummaryValueMuted>{notApplicable}</ConfirmSummaryValueMuted>
          )}
        </ConfirmSummaryRow>
        <ConfirmSummaryRow>
          <ConfirmSummaryLabel>{labels.labelMotive}</ConfirmSummaryLabel>
          <ConfirmSummaryValue>{reasonSelected?.reasonValue}</ConfirmSummaryValue>
        </ConfirmSummaryRow>
        <ConfirmSummaryRow>
          <ConfirmSummaryLabel>{labels.labelGroup}</ConfirmSummaryLabel>
          <ConfirmSummaryValue>
            {reasonSelected?.categoryValue ?? notApplicable}
          </ConfirmSummaryValue>
        </ConfirmSummaryRow>
        <ConfirmSummaryRow>
          <ConfirmSummaryLabel>{labels.labelFailureMode}</ConfirmSummaryLabel>
          {reasonSelected?.failureModes?.[0]?.name ? (
            <ConfirmSummaryValue>{reasonSelected.failureModes[0].name}</ConfirmSummaryValue>
          ) : (
            <ConfirmSummaryValueMuted>{notApplicable}</ConfirmSummaryValueMuted>
          )}
        </ConfirmSummaryRow>
      </ConfirmSummaryCard>

      <NoteContainer>
        <AllieText type="body2" weight={500} color="neutral-90">
          {labels.note}
          {commentRequired && <RequiredAsterisk>*</RequiredAsterisk>}
        </AllieText>
        <TextAreaNoMargin
          corner="rounded-edges-plus"
          onChange={(ev: any) => setNote(ev.target.value)}
          placeholder={labels.notePlaceholder}
        />
      </NoteContainer>

      <QuickConfirmFooter>
        <StyledCancelButton
          allieSize={isTablet ? 'medium' : 'small'}
          corner="rounded-edges"
          onclick={onGoPrev}
        >
          {labels.back}
        </StyledCancelButton>
        <AllieButton
          allieSize={isTablet ? 'medium' : 'small'}
          corner="rounded-edges"
          disabled={!isNoteValid}
          onclick={() => onConfirm(note)}
        >
          {labels.confirm}
        </AllieButton>
      </QuickConfirmFooter>
    </ContentLoadingStyled>
  );
};
